import {supabase} from '@/integrations/supabase/client';
import {platformOpen} from '@/lib/platformOpen';
import {creatorConfig, CREATOR_PROVIDERS, type CreatorProvider} from '../../supabase/functions/_shared/creatorDomain';
export {CREATOR_PROVIDERS};
export type {CreatorProvider};
export interface CreatorConnectionView {
  provider: CreatorProvider; status: 'pending' | 'active' | 'expired'; account_id: string | null;
  can_publish?: boolean; display_name: string | null; last_verified_at: string | null;
  summary: {credits?: number; connectors?: {id: string; accounts: number; actions: string[]}[]};
}
export interface CreatorList {
  user_id: string; connections: CreatorConnectionView[];
  providers: {provider: CreatorProvider; available: boolean; operations?: boolean}[];
}
function creatorLogin(token: string): string | null {
  try { const part=token.split('.')[1];const claims=JSON.parse(atob(part.replace(/-/g,'+').replace(/_/g,'/')));
    return typeof claims.session_id==='string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(claims.session_id) ? claims.session_id : null;
  } catch {return null;}
}
async function creatorSession(owner: string) {
  const {data: {session}, error} = await supabase.auth.getSession();
  if (error || session?.user.id !== owner || !session.access_token) throw new Error('invalid_session');
  return {token:session.access_token,sid:creatorLogin(session.access_token)};
}
export async function creatorRequest<T>(owner: string, action: string, provider?: CreatorProvider, input: Record<string,unknown> = {}): Promise<T> {
  const session = await creatorSession(owner);
  const {data, error} = await supabase.functions.invoke('creator-connections', {
    headers: {Authorization: 'Bearer ' + session.token}, body: {...input, action, ...(provider ? {provider} : {})},
  });
  const current = await creatorSession(owner);
  // A new login by the same owner must not receive the previous session's result.
  if (session.sid ? current.sid!==session.sid : current.token!==session.token) throw new Error('invalid_session');
  if (error) {
    let code = 'request_failed';
    try { code = (await error.context?.json())?.error || code; } catch { /* offline; preserve retry state */ }
    throw new Error(code);
  }
  if (!data || data.user_id !== owner) throw new Error('invalid_session');
  return data as T;
}
export async function connectCreator(owner: string, provider: CreatorProvider, write = false) {
  const data = await creatorRequest<{authorize_url: string}>(owner, write ? 'connect_write' : 'connect', provider);
  const url = new URL(data.authorize_url), allowed = new URL(creatorConfig[provider].authorize);
  if (url.origin !== allowed.origin || url.pathname !== allowed.pathname || url.username || url.password || url.hash) {
    throw new Error('invalid_authorize_url');
  }
  await creatorSession(owner);
  await platformOpen(url.href);
}
